import React from "react";
import { useForm, useWatch } from "react-hook-form";
import { useWorkflowsBuilderPageView } from "../../pages/WorkflowsBuilderPage/WorkflowsBuilderPage.context";
import { useDrawerInvokers } from "@tiny/lib";
import { useConfigView } from "../../Config.context";
import { useGetCustomerTagsQuery } from "../../app/useGeneratedApi";
import { useAudienceData } from "../TriggerForm/SelectRules/AudienceSelector/AudienceSelector.hooks";
import {
  NodeData,
  Node,
} from "../../pages/WorkflowsBuilderPage/WorkflowsBuilder/types";
import { getParentNodes } from "../../pages/WorkflowsBuilderPage/WorkflowBuilderPage.utils";
import {
  formatISO8601Duration,
  parseISO8601Duration,
} from "../../utils/dateUtils";

export const transformNodeToFormValues = (nodeData: NodeData | undefined) => {
  if (nodeData?.condition) {
    const baseCondition =
      nodeData.condition.conditions[1]?.conditions[0]?.conditions[0];

    const timeout = parseISO8601Duration(nodeData?.timeout || "");

    if (baseCondition?.attributeType === "segment-activity") {
      const isTagCondition = baseCondition?.attributeName === "EXIST_TAGS";
      return {
        ruleType: "segment-activity",
        event: baseCondition?.attributeName || "",
        audiences: isTagCondition ? [] : baseCondition?.criteria || [],
        tags: isTagCondition ? baseCondition?.criteria || [] : [],
        email: "",
        links: [],
        from: "start-of-workflow",
        timeoutDays: timeout.days,
        timeoutHours: timeout.hours,
      };
    }

    return {
      ruleType: baseCondition?.attributeType || "",
      event: baseCondition?.attributeName || "",
      email:
        baseCondition?.attributeType === "marketing-activity"
          ? baseCondition?.criteria
          : "",
      audiences: [],
      tags: [],
      links:
        baseCondition?.attributeType === "marketing-activity"
          ? (baseCondition as any)?.links || []
          : [],
      from: "start-of-workflow",
      timeoutDays: timeout.days,
      timeoutHours: timeout.hours,
    };
  }
  return {
    ruleType: "",
    event: "",
    email: "",
    audiences: [],
    tags: [],
    links: [],
    from: "start-of-workflow",
    timeoutDays: 10,
    timeoutHours: 0,
  };
};

const useConditionalSplitForm = ({ id }: { id: string }) => {
  const { updateNode, getNode, setWorkflowData, viewMode, workflowData } =
    useWorkflowsBuilderPageView();
  const { hideDrawer } = useDrawerInvokers();
  const { config } = useConfigView();
  const node = getNode(id);
  const [saveRulesDisabled, setSaveRulesDisabled] = React.useState(true);

  const isTinyEmail = config?.appName === "tinyEmail";
  const isTinyAlbert = config?.appName === "tinyAlbert";

  const emailOptions = getEmailOptions(
    getParentNodes(id, workflowData.structure),
    workflowData.id
  );

  const methods = useForm({
    defaultValues: transformNodeToFormValues(node?.data),
    mode: "onChange",
  });

  const { handleSubmit, control, reset, getValues, setValue } = methods;
  const formValues = useWatch({ control });

  const { data: tagsData } = useGetCustomerTagsQuery();

  const audienceData = useAudienceData({
    node,
    isTinyEmail,
    isTinyAlbert,
    setValue: setValue as any,
    albertAudienceTypes: ["FORM", "UPLOAD"],
    formFieldName: "audiences",
  });

  const goBack = () => {
    setWorkflowData((prev) => ({
      ...prev,
      triggerType: undefined,
    }));
    hideDrawer();
  };

  const onSubmit = handleSubmit(async (data) => {
    let nodeData;

    if (data.ruleType === "segment-activity") {
      const isTagCondition = data.event === "EXIST_TAGS";
      const criteria = isTagCondition
        ? data.tags
        : data.audiences.map((aud: any) => aud.id);

      nodeData = {
        logicType: isTagCondition ? "EXIST_TAGS" : "EXISTS_IN_AUDIENCE",
        condition: {
          type: "combined",
          conditionType: "AND",
          conditions: [
            {
              type: "combined",
              conditionType: "AND",
              conditions: [],
            },
            {
              type: "combined",
              conditionType: "OR",
              conditions: [
                {
                  type: "combined",
                  conditionType: "AND",
                  conditions: [
                    {
                      type: "base",
                      attributeType: data.ruleType,
                      attributeName: data.event,
                      criteria: criteria,
                    },
                  ],
                },
              ],
            },
          ],
        },
        conditionType: "segment-activity",
        timeout: undefined,
      };
    } else if (data.ruleType === "marketing-activity") {
      const timeoutDuration = formatISO8601Duration(
        data.timeoutDays || 0,
        data.timeoutHours || 0
      );
      nodeData = {
        condition: {
          type: "combined",
          conditionType: "AND",
          conditions: [
            {
              type: "combined",
              conditionType: "AND",
              conditions: [],
            },
            {
              type: "combined",
              conditionType: "OR",
              conditions: [
                {
                  type: "combined",
                  conditionType: "AND",
                  conditions: [
                    {
                      type: "base",
                      attributeType: data.ruleType,
                      attributeName: data.event,
                      criteria: data.email,
                      links: data.links || [],
                    },
                  ],
                },
              ],
            },
          ],
        },
        conditionType: "marketing-activity",
        timeout: timeoutDuration,
      };
    } else {
      nodeData = {
        condition: {
          type: "combined",
          conditionType: "AND",
          conditions: [
            {
              type: "combined",
              conditionType: "AND",
              conditions: [],
            },
            {
              type: "combined",
              conditionType: "OR",
              conditions: [
                {
                  type: "combined",
                  conditionType: "AND",
                  conditions: [
                    {
                      type: "base",
                      attributeType: data.ruleType,
                      attributeName: data.event,
                      criteria: "SINCE_BEGINING",
                    },
                  ],
                },
              ],
            },
          ],
        },
        conditionType: undefined,
        timeout: undefined,
      };
    }

    updateNode({
      id,
      data: {
        ...nodeData,
        config: {
          configured: true,
        },
      },
    });
    hideDrawer();
  });

  React.useEffect(() => {
    const getIncomingState = () => {
      if (node?.data?.condition) {
        const baseCondition =
          node.data.condition.conditions[1]?.conditions[0]?.conditions[0];
        const timeout = parseISO8601Duration(baseCondition?.timeout || "");

        if (baseCondition?.attributeType === "segment-activity") {
          const isTagCondition = baseCondition?.attributeName === "EXIST_TAGS";
          return {
            ruleType: "segment-activity",
            event: baseCondition?.attributeName || "",
            audiences: isTagCondition ? [] : baseCondition?.criteria || [],
            tags: isTagCondition ? baseCondition?.criteria || [] : [],
            email: "",
            links: [],
            from: "start-of-workflow",
            timeoutDays: timeout.days,
            timeoutHours: timeout.hours,
          };
        }

        return {
          ruleType: baseCondition?.attributeType || "",
          event: baseCondition?.attributeName || "",
          email:
            baseCondition?.attributeType === "marketing-activity"
              ? baseCondition?.criteria
              : "",
          audiences: [],
          tags: [],
          links:
            baseCondition?.attributeType === "marketing-activity"
              ? (baseCondition as any)?.links || []
              : [],
          from: "start-of-workflow",
          timeoutDays: timeout.days,
          timeoutHours: timeout.hours,
        };
      }
      return {
        ruleType: "",
        event: "",
        email: "",
        audiences: [],
        tags: [],
        links: [],
        from: "start-of-workflow",
        timeoutDays: 10,
        timeoutHours: 0,
      };
    };

    const incomingState = JSON.stringify(getIncomingState());

    const currentState = JSON.stringify({
      ruleType: formValues.ruleType || "",
      event: formValues.event || "",
      email: formValues.email || "",
      audiences: formValues.audiences || [],
      tags: formValues.tags || [],
      links: formValues.links || [],
      from: "start-of-workflow",
      timeoutDays: formValues.timeoutDays || 0,
      timeoutHours: formValues.timeoutHours || 0,
    });

    let isComplete = false;
    if (formValues.ruleType === "marketing-activity") {
      isComplete = formValues.ruleType && formValues.email && formValues.event;
    } else if (formValues.ruleType === "segment-activity") {
      const isTagCondition = formValues.event === "EXIST_TAGS";
      isComplete =
        formValues.ruleType &&
        formValues.event &&
        (isTagCondition
          ? formValues.tags?.length > 0
          : formValues.audiences?.length > 0);
    } else {
      isComplete = formValues.ruleType && formValues.event;
    }

    setSaveRulesDisabled(!isComplete || incomingState === currentState);
  }, [node, formValues]);

  return {
    goBack,
    onSubmit,
    methods,
    formValues,
    control,
    saveRulesDisabled,
    viewMode,
    emailOptions,
    reset,
    getValues,
    isTinyEmail,
    tags: tagsData?.tags || [],
    ...audienceData,
  };
};

export const getEmailOptions = (
  workflowData: Array<Node | undefined>,
  workflow_id: string
) =>
  workflowData.length
    ? workflowData
        ?.filter(
          (node) => node?.type === "ACTION" && node?.data?.config?.configured
        )
        ?.map((node) => ({
          name: node?.data?.name,
          workflow_id,
          template_id: node?.data?.template_id,
          node_id: node?.id,
        }))
    : [];

export default useConditionalSplitForm;
